(function () {
  'use strict';

  // ── Config ─────────────────────────────────────────────────────────────────
  const ATTR_DATE   = 'Pickup Date';
  const ATTR_TIME   = 'Pickup Time';
  const ATTR_EXPIRY = 'Pickup Expiry';
  const EXPIRY_MS   = 20 * 60 * 1000; // 20 minutes

  const TIME_SLOTS = [
    '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM',  '2:00 PM',  '3:00 PM',
    '4:00 PM',  '5:00 PM',  '6:00 PM',
  ];

  // ── Full pickup slot overrides ─────────────────────────────────────────────
  // Key = YYYY-MM-DD, value = array of time strings exactly as in TIME_SLOTS.
  // General full slots. These apply globally.
  const FULL_PICKUP_SLOTS = {
    '2026-05-10': ['12:00 PM'],
  };

  // Standard cake full slots.
  // These apply only when the cart/order is NOT Mother's Day.
  const STANDARD_FULL_PICKUP_SLOTS = {
    '2026-05-09': ['12:00 PM', '1:00 PM'],
  };

  function isGeneralSlotFull(date, timeSlot) {
    if (!date || !FULL_PICKUP_SLOTS[date]) return false;
    return FULL_PICKUP_SLOTS[date].indexOf(timeSlot) !== -1;
  }

  function isStandardSlotFull(date, timeSlot) {
    if (!date || !STANDARD_FULL_PICKUP_SLOTS[date]) return false;
    return STANDARD_FULL_PICKUP_SLOTS[date].indexOf(timeSlot) !== -1;
  }

  function isPickupSlotFull(date, timeSlot, isStandardOrder) {
    if (isGeneralSlotFull(date, timeSlot)) return true;
    if (isStandardOrder && isStandardSlotFull(date, timeSlot)) return true;
    return false;
  }

  function dateHasAnyFullSlot(date, isStandardOrder) {
    if (!date) return false;
    if (FULL_PICKUP_SLOTS[date] && FULL_PICKUP_SLOTS[date].length > 0) return true;
    if (isStandardOrder && STANDARD_FULL_PICKUP_SLOTS[date] && STANDARD_FULL_PICKUP_SLOTS[date].length > 0) return true;
    return false;
  }

  function getFullSlotMessage(isStandardOrder) {
    return isStandardOrder
      ? 'Some pickup times are already full. Please choose another available pickup time.'
      : 'Mother’s Day pickup slots are filling quickly. Some time slots may close once full.';
  }
  // ── End full slot overrides ────────────────────────────────────────────────

  // ── Assorted Cake rule ─────────────────────────────────────────────────────
  // Any cart containing an Assorted Cake must have a pickup time:
  //   Tue–Fri (day 2–5): >= 12:00 PM
  //   Sat    (day 6)   : >= 1:00 PM
  // Sun/Mon are closed by the broader open-day logic.
  const ASSORTED_CAKE_MIN_MINUTES_WEEKDAY = 720; // 12:00 PM (Tue–Fri)
  const ASSORTED_CAKE_MIN_MINUTES_SAT     = 780; // 1:00 PM (Sat)

  // ymd is a 'YYYY-MM-DD' string (the format used in cart attributes). Falls
  // back to the weekday minimum when the date isn't yet known.
  function assortedMinMinutesForDateYMD(ymd) {
    if (!ymd) return ASSORTED_CAKE_MIN_MINUTES_WEEKDAY;
    const parts = String(ymd).split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return ASSORTED_CAKE_MIN_MINUTES_WEEKDAY;
    return new Date(parts[0], parts[1] - 1, parts[2]).getDay() === 6
      ? ASSORTED_CAKE_MIN_MINUTES_SAT
      : ASSORTED_CAKE_MIN_MINUTES_WEEKDAY;
  }

  function assortedMinLabelForDateYMD(ymd) {
    return assortedMinMinutesForDateYMD(ymd) === ASSORTED_CAKE_MIN_MINUTES_SAT ? '1:00 PM' : '12:00 PM';
  }

  function timeToMinutes(slot) {
    // Parses "H:MM AM/PM" -> minutes after midnight.
    const m = String(slot).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return 0;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const mer = m[3].toUpperCase();
    if (mer === 'PM' && h !== 12) h += 12;
    if (mer === 'AM' && h === 12) h = 0;
    return h * 60 + min;
  }

  function cartHasAssortedCake(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function(item) {
      const title  = (item.product_title || '').toLowerCase();
      const handle = (item.handle || '').toLowerCase();
      return title.includes('assorted cake') || handle.includes('assorted');
    });
  }
  // ── End Assorted Cake rule ─────────────────────────────────────────────────

  // ── Mother's Day rule ──────────────────────────────────────────────────────
  // Mother's Day cakes are available for pickup on May 9 and May 10.
  const MD_VALID_DATES = new Set(['2026-05-09', '2026-05-10']);

  function isMothersDayText(title, handle) {
    title = (title || '').toLowerCase();
    handle = (handle || '').toLowerCase();

    return handle.indexOf('mother-s-day') !== -1 ||
           title.indexOf('mother') !== -1 ||
           title.indexOf('carnation') !== -1 ||
           title.indexOf('rose cake') !== -1 ||
           title.indexOf('mini rose') !== -1 ||
           title.indexOf('blush bouquet') !== -1;
  }

  function cartHasMothersDay(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function(item) {
      return isMothersDayText(item.product_title, item.handle);
    });
  }

  function productFormIsMothersDay(form) {
    const productForm = document.getElementById('product-form') || form;
    if (!productForm) return false;

    const handle = (productForm.dataset.productHandle || '').toLowerCase();
    const title  = (productForm.dataset.productTitle || '').toLowerCase();

    return isMothersDayText(title, handle);
  }
  // ── End Mother's Day rule ─────────────────────────────────────────────────

  // ── Cart API ───────────────────────────────────────────────────────────────
  async function getCart() {
    return fetch('/cart.js').then(function(r) { return r.json(); });
  }

  async function updateCartAttrs(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    }).then(function(r) { return r.json(); });
  }

  // ── Pickup state ───────────────────────────────────────────────────────────
  function isExpired(str) {
    return !str || Date.now() > parseInt(str, 10);
  }

  async function getCartPickup() {
    const cart = await getCart();
    if (cart.item_count === 0) return null;
    const date   = cart.attributes && cart.attributes[ATTR_DATE];
    const time   = cart.attributes && cart.attributes[ATTR_TIME];
    const expiry = cart.attributes && cart.attributes[ATTR_EXPIRY];
    if (!date || !time || isExpired(expiry)) return null;
    return { date, time, expiry };
  }

  async function setCartPickup(date, time) {
    const expiry = String(Date.now() + EXPIRY_MS);
    await updateCartAttrs({ [ATTR_DATE]: date, [ATTR_TIME]: time, [ATTR_EXPIRY]: expiry });
    return expiry;
  }

  // ── Formatting ─────────────────────────────────────────────────────────────
  function fmtDate(iso) {
    const parts = iso.split('-').map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
    });
  }

  function fmtExpiry(str) {
    return new Date(parseInt(str, 10)).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  function getAvailableDates() {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      const y  = d.getFullYear();
      const m  = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      out.push({
        value: y + '-' + m + '-' + dd,
        label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      });
    }
    return out;
  }

  // ── Picker HTML ────────────────────────────────────────────────────────────
  // restrict2pm: when true, hide TIME_SLOTS before 2:00 PM.
  // isStandardOrder: when true, block Standard-only full slots.
  function getTimeOptionsHTML(selDate, selTime, restrict2pm, isStandardOrder) {
    let slots = TIME_SLOTS;
    if (restrict2pm) {
      const minMinutes = assortedMinMinutesForDateYMD(selDate);
      slots = slots.filter(function(t) { return timeToMinutes(t) >= minMinutes; });
    }

    return slots.map(function(t) {
      const full = isPickupSlotFull(selDate, t, isStandardOrder);
      const label = full ? t + ' — Full' : t;
      const attrs = (!full && t === selTime ? ' selected' : '') + (full ? ' disabled class="hc-slot-full"' : '');
      return '<option value="' + t + '"' + attrs + '>' + label + '</option>';
    }).join('');
  }

  function pickerHTML(prefix, selDate, selTime, restrict2pm, isStandardOrder) {
    restrict2pm = !!restrict2pm;
    isStandardOrder = !!isStandardOrder;

    const dateOpts = getAvailableDates().map(function(d) {
      return '<option value="' + d.value + '"' + (d.value === selDate ? ' selected' : '') + '>' + d.label + '</option>';
    }).join('');

    const timeOpts = getTimeOptionsHTML(selDate, selTime, restrict2pm, isStandardOrder);
    const urgencyStyle = dateHasAnyFullSlot(selDate, isStandardOrder) ? '' : ' style="display:none"';

    return '<div class="hc-pickup-row">' +
      '<label class="hc-pickup-label" for="' + prefix + '-date">Pickup Date</label>' +
      '<select class="hc-pickup-select" id="' + prefix + '-date">' +
      '<option value="">Select a date</option>' + dateOpts +
      '</select></div>' +
      '<div class="hc-pickup-row">' +
      '<label class="hc-pickup-label" for="' + prefix + '-time">Pickup Time</label>' +
      '<select class="hc-pickup-select" id="' + prefix + '-time">' +
      '<option value="">Select a time</option>' + timeOpts +
      '</select></div>' +
      '<p class="hc-pickup-urgency" id="' + prefix + '-urgency"' + urgencyStyle + '>' + getFullSlotMessage(isStandardOrder) + '</p>';
  }

  function bindPickupDateRefresh(prefix, restrict2pm, isStandardOrder) {
    const dateEl = document.getElementById(prefix + '-date');
    const timeEl = document.getElementById(prefix + '-time');
    const urgencyEl = document.getElementById(prefix + '-urgency');

    if (!dateEl || !timeEl) return;

    dateEl.addEventListener('change', function() {
      const selectedDate = dateEl.value;
      const currentTime = timeEl.value;

      timeEl.innerHTML =
        '<option value="">Select a time</option>' +
        getTimeOptionsHTML(selectedDate, currentTime, restrict2pm, isStandardOrder);

      if (urgencyEl) {
        if (dateHasAnyFullSlot(selectedDate, isStandardOrder)) {
          urgencyEl.textContent = getFullSlotMessage(isStandardOrder);
          urgencyEl.style.display = '';
        } else {
          urgencyEl.style.display = 'none';
        }
      }
    });
  }

  // ── Mismatch modal ─────────────────────────────────────────────────────────
  function showMismatchModal(cDate, cTime, nDate, nTime, onKeep, onChange, onCancel) {
    const existing = document.getElementById('hc-pickup-modal');
    if (existing) existing.remove();

    const el = document.createElement('div');
    el.id = 'hc-pickup-modal';
    el.className = 'hc-modal-overlay';
    el.innerHTML =
      '<div class="hc-modal">' +
      '<h3 class="hc-modal__title">Pickup time conflict</h3>' +
      '<p class="hc-modal__text">Your cart already has a pickup for <strong>' + fmtDate(cDate) + ' at ' + cTime + '</strong>.</p>' +
      '<p class="hc-modal__text">Each order can only have one pickup slot. You selected <strong>' + fmtDate(nDate) + ' at ' + nTime + '</strong> for this item.</p>' +
      '<div class="hc-modal__btns">' +
      '<button class="hc-modal__btn hc-modal__btn--primary" id="hc-modal-keep">Keep ' + cTime + ' on ' + fmtDate(cDate) + '</button>' +
      '<button class="hc-modal__btn hc-modal__btn--outline" id="hc-modal-change">Change entire order to ' + nTime + ' on ' + fmtDate(nDate) + '</button>' +
      '<button class="hc-modal__btn hc-modal__btn--ghost" id="hc-modal-cancel">Cancel</button>' +
      '</div></div>';

    document.body.appendChild(el);

    el.querySelector('#hc-modal-keep').onclick = function() { el.remove(); onKeep(); };
    el.querySelector('#hc-modal-change').onclick = function() { el.remove(); onChange(); };
    el.querySelector('#hc-modal-cancel').onclick = function() { el.remove(); onCancel(); };
    el.addEventListener('click', function(e) { if (e.target === el) { el.remove(); onCancel(); } });
  }

  // ── Checkout gate ──────────────────────────────────────────────────────────
  function lockCheckout(msg) {
    document.querySelectorAll('[name="checkout"], .cart__checkout-button, [href="/checkout"]').forEach(function(b) {
      b.disabled = true;
      b.setAttribute('data-hc-locked', '1');
    });

    let p = document.getElementById('hc-checkout-msg');
    if (!p) {
      p = document.createElement('p');
      p.id = 'hc-checkout-msg';
      p.className = 'hc-checkout-msg';
      const btn = document.querySelector('[name="checkout"], .cart__checkout-button');
      if (btn) btn.before(p);
    }

    p.textContent = msg || 'Select a pickup time to continue.';
  }

  function unlockCheckout() {
    document.querySelectorAll('[data-hc-locked]').forEach(function(b) {
      b.disabled = false;
      b.removeAttribute('data-hc-locked');
    });
    const msg = document.getElementById('hc-checkout-msg');
    if (msg) msg.remove();
  }

  // ── Expiry watcher ─────────────────────────────────────────────────────────
  let expiryTimer = null;

  function watchExpiry(expiryStr, onExpire) {
    clearTimeout(expiryTimer);
    const ms = parseInt(expiryStr, 10) - Date.now();
    if (ms <= 0) { onExpire(); return; }
    expiryTimer = setTimeout(onExpire, Math.min(ms, 2147483647));
  }

  // ── Product page ───────────────────────────────────────────────────────────
  async function initProduct() {
    const wrap = document.getElementById('hc-pickup-product');
    if (!wrap) return;

    wrap.innerHTML = '<p class="hc-pickup-loading">Loading pickup options…</p>';

    const existing = await getCartPickup();

    const form = wrap.closest('form[action="/cart/add"]') || document.querySelector('form[action="/cart/add"]');
    if (!form) return;

    const productIsMothersDay = productFormIsMothersDay(form);
    const productIsStandardOrder = !productIsMothersDay;

    wrap.innerHTML =
      '<div class="hc-pickup-card">' +
      '<p class="hc-pickup-section-label">Pickup Details</p>' +
      (existing
        ? '<p class="hc-pickup-existing-note">Your cart already has a pickup for <strong>' + fmtDate(existing.date) + ' at ' + existing.time + '</strong>. Adding a cake at a different time will prompt you to confirm.</p>'
        : '') +
      pickerHTML('prod', existing ? existing.date : '', existing ? existing.time : '', false, productIsStandardOrder) +
      '<p class="hc-pickup-error" id="hc-product-error" style="display:none"></p>' +
      '</div>';

    bindPickupDateRefresh('prod', false, productIsStandardOrder);

    const dateEl = document.getElementById('prod-date');
    const timeEl = document.getElementById('prod-time');
    const errEl  = document.getElementById('hc-product-error');

    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      errEl.style.display = 'none';

      const newDate = dateEl.value;
      const newTime = timeEl.value;

      if (!newDate || !newTime) {
        errEl.textContent = 'Please select a pickup date and time.';
        errEl.style.display = 'block';
        dateEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (isPickupSlotFull(newDate, newTime, productIsStandardOrder)) {
        errEl.textContent = 'This pickup time is now full. Please select a different time.';
        errEl.style.display = 'block';
        timeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const cart  = await getCart();
      const eDate = cart.attributes && cart.attributes[ATTR_DATE];
      const eTime = cart.attributes && cart.attributes[ATTR_TIME];
      const eExpiry = cart.attributes && cart.attributes[ATTR_EXPIRY];
      const hasItems = cart.item_count > 0;

      const cartIsMothersDay = cartHasMothersDay(cart);
      const cartIsStandardOrder = !cartIsMothersDay;

      if (hasItems && eDate && eTime && !isExpired(eExpiry) && isPickupSlotFull(eDate, eTime, cartIsStandardOrder)) {
        errEl.textContent = 'Your cart has a pickup time that is now full. Please update the pickup time in cart before adding more items.';
        errEl.style.display = 'block';
        return;
      }

      if (hasItems && eDate && !isExpired(eExpiry) && (eDate !== newDate || eTime !== newTime)) {
        showMismatchModal(
          eDate, eTime, newDate, newTime,
          async function() { dateEl.value = eDate; timeEl.value = eTime; await doAddToCart(form, eDate, eTime); },
          async function() { await doAddToCart(form, newDate, newTime); },
          function() {}
        );
        return;
      }

      await doAddToCart(form, newDate, newTime);
    }, true);
  }

  async function doAddToCart(form, date, time) {
    const btn  = form.querySelector('[type="submit"]');
    const orig = btn && btn.textContent;
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    try {
      const r = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
      const result = await r.json();

      if (result.status) {
        if (btn) { btn.disabled = false; btn.textContent = orig; }
        alert(result.description || 'Could not add to cart.');
        return;
      }

      await setCartPickup(date, time);
      window.location.href = '/cart';
    } catch (err) {
      console.error('[pickup] add-to-cart error', err);
      if (btn) { btn.disabled = false; btn.textContent = orig; }
    }
  }

  // ── Cart page ──────────────────────────────────────────────────────────────
  async function initCart() {
    const wrap = document.getElementById('hc-pickup-cart');
    if (!wrap) return;

    const cart = await getCart();
    if (cart.item_count === 0) { wrap.innerHTML = ''; return; }

    const restrict2pm = cartHasAssortedCake(cart);
    const cartMD = cartHasMothersDay(cart);
    const isStandardOrder = !cartMD;

    const date   = cart.attributes && cart.attributes[ATTR_DATE];
    const time   = cart.attributes && cart.attributes[ATTR_TIME];
    const expiry = cart.attributes && cart.attributes[ATTR_EXPIRY];

    // If cart has Assorted Cake and the saved time is before the date-aware
    // minimum (Tue–Fri < 12 PM, Sat < 1 PM), invalidate it.
    const timeIsInvalid = restrict2pm && time && date && timeToMinutes(time) < assortedMinMinutesForDateYMD(date);

    // Mother's Day date check.
    const mdDateIsWrong = cartMD && date && !MD_VALID_DATES.has(date);

    // Full slot check.
    const selectedSlotIsFull = date && time && isPickupSlotFull(date, time, isStandardOrder);

    if (!date || !time || isExpired(expiry) || timeIsInvalid || mdDateIsWrong || selectedSlotIsFull) {
      const expiredMsg = mdDateIsWrong
        ? 'Mother\'s Day cakes are available for pickup on Saturday, May 9 or Sunday, May 10 only. Please update the pickup date before checkout.'
        : timeIsInvalid
        ? 'Your cart contains Assorted Cake — please select a pickup time of ' + assortedMinLabelForDateYMD(date) + ' or later to continue.'
        : selectedSlotIsFull
        ? 'This pickup time is now full. Please select a different pickup time before checkout.'
        : 'Your pickup selection has expired. Please choose a new time to continue.';

      renderCartEditor(wrap, '', '', true, restrict2pm, expiredMsg, isStandardOrder);
      lockCheckout(expiredMsg);
    } else {
      renderCartDisplay(wrap, date, time, expiry);
      unlockCheckout();

      watchExpiry(expiry, function() {
        renderCartEditor(wrap, '', '', true, restrict2pm,
          'Your pickup selection has expired. Please choose a new time to continue.',
          isStandardOrder
        );
        lockCheckout('Your pickup selection has expired. Please choose a new time to continue.');
      });
    }
  }

  function renderCartDisplay(wrap, date, time, expiry) {
    wrap.innerHTML =
      '<div class="hc-cart-pickup">' +
      '<div class="hc-cart-pickup__info">' +
      '<span class="hc-cart-pickup__label">Pickup</span>' +
      '<span class="hc-cart-pickup__val">' + fmtDate(date) + ' at ' + time + '</span>' +
      '<span class="hc-cart-pickup__exp">Reserved until ' + fmtExpiry(expiry) + '</span>' +
      '</div>' +
      '<button class="hc-cart-pickup__change" id="hc-pickup-change">Change</button>' +
      '</div>';

    document.getElementById('hc-pickup-change').addEventListener('click', async function() {
      const cart = await getCart();
      const restrict2pm = cartHasAssortedCake(cart);
      const isStandardOrder = !cartHasMothersDay(cart);
      renderCartEditor(wrap, date, time, false, restrict2pm, null, isStandardOrder);
    });
  }

  function renderCartEditor(wrap, date, time, expired, restrict2pm, customMsg, isStandardOrder) {
    isStandardOrder = !!isStandardOrder;

    const headingText = customMsg
      || (expired ? '⚠ Pickup selection expired — choose a new time to continue' : 'Change pickup time');
    const headingCls = 'hc-cart-editor__heading' + (expired || customMsg ? ' hc-cart-editor__heading--warn' : '');

    wrap.innerHTML =
      '<div class="hc-cart-editor">' +
      '<p class="' + headingCls + '">' + headingText + '</p>' +
      pickerHTML('cart', date, time, restrict2pm, isStandardOrder) +
      '<button class="hc-cart-editor__save" id="hc-cart-save">Confirm pickup</button>' +
      (!expired ? '<button class="hc-cart-editor__cancel" id="hc-cart-cancel">Cancel</button>' : '') +
      '</div>';

    bindPickupDateRefresh('cart', restrict2pm, isStandardOrder);

    document.getElementById('hc-cart-save').addEventListener('click', async function(e) {
      const btn   = e.currentTarget;
      const nDate = document.getElementById('cart-date').value;
      const nTime = document.getElementById('cart-time').value;

      if (!nDate || !nTime) {
        alert('Please select both a date and time.');
        return;
      }

      // Assorted Cake rule: validate selected time meets the date-aware minimum.
      // Tue–Fri: >= 12 PM. Sat: >= 1 PM.
      if (restrict2pm && timeToMinutes(nTime) < assortedMinMinutesForDateYMD(nDate)) {
        alert('Your cart contains Assorted Cake. Please select a pickup time of ' + assortedMinLabelForDateYMD(nDate) + ' or later.');
        return;
      }

      const cart2 = await getCart();
      const cart2HasMD = cartHasMothersDay(cart2);
      const cart2IsStandardOrder = !cart2HasMD;

      // Mother's Day date guard.
      if (cart2HasMD && !MD_VALID_DATES.has(nDate)) {
        alert('Mother’s Day cakes are available for pickup on Saturday, May 9 or Sunday, May 10 only. Please select one of those dates.');
        btn.disabled = false;
        btn.textContent = 'Confirm pickup';
        return;
      }

      // Full slot guard.
      if (isPickupSlotFull(nDate, nTime, cart2IsStandardOrder)) {
        alert('This pickup time is now full. Please select a different time.');
        btn.disabled = false;
        btn.textContent = 'Confirm pickup';
        return;
      }

      btn.disabled = true;
      btn.textContent = 'Saving…';

      const newExpiry = await setCartPickup(nDate, nTime);
      renderCartDisplay(wrap, nDate, nTime, newExpiry);
      unlockCheckout();

      watchExpiry(newExpiry, function() {
        renderCartEditor(wrap, '', '', true, restrict2pm,
          'Your pickup selection has expired. Please choose a new time to continue.',
          cart2IsStandardOrder
        );
        lockCheckout('Your pickup selection has expired. Please choose a new time to continue.');
      });
    });

    const cancelBtn = document.getElementById('hc-cart-cancel');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', async function() {
        const cart = await getCart();
        const d   = cart.attributes && cart.attributes[ATTR_DATE];
        const t   = cart.attributes && cart.attributes[ATTR_TIME];
        const exp = cart.attributes && cart.attributes[ATTR_EXPIRY];
        if (d && t && exp && !isExpired(exp)) { renderCartDisplay(wrap, d, t, exp); }
      });
    }
  }

  // ── Boot ───────────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function() {
    if (document.getElementById('hc-pickup-product')) initProduct();
    if (document.getElementById('hc-pickup-cart'))    initCart();
  });
})();